import React, { useState, useEffect, useMemo } from 'react';
import { Save, Plus, X, Settings, Database, Users, Tag, Coins, Shield, ChevronUp, ChevronDown, Pencil, Trash2, AlertCircle, ThumbsUp, HelpCircle, Star, Zap, Eye } from 'lucide-react';
import { AppSettings, CurrencyRate, CriticalityLevel, DEFAULT_CRITICALITY_LEVELS } from '../types';
import { InfoTooltip } from '../components/InfoTooltip';

const CRITICALITY_COLORS = [
  { id: 'red', label: 'Red', bg: 'bg-red-100', border: 'border-red-200', text: 'text-red-600' },
  { id: 'orange', label: 'Orange', bg: 'bg-orange-100', border: 'border-orange-200', text: 'text-orange-600' },
  { id: 'amber', label: 'Amber', bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-600' },
  { id: 'emerald', label: 'Green', bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-600' },
  { id: 'blue', label: 'Blue', bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-600' },
  { id: 'purple', label: 'Purple', bg: 'bg-purple-100', border: 'border-purple-200', text: 'text-purple-600' },
];

const CRITICALITY_ICONS: { id: string; label: string; Icon: React.FC<any> }[] = [
  { id: 'AlertCircle', label: 'Alert', Icon: AlertCircle },
  { id: 'Zap', label: 'Zap', Icon: Zap },
  { id: 'Star', label: 'Star', Icon: Star },
  { id: 'ThumbsUp', label: 'Thumbs Up', Icon: ThumbsUp },
  { id: 'Eye', label: 'Eye', Icon: Eye },
  { id: 'HelpCircle', label: 'Help', Icon: HelpCircle },
];

function getCriticalityColorClasses(colorId: string) {
  return CRITICALITY_COLORS.find(c => c.id === colorId) ?? CRITICALITY_COLORS[0];
}

function getCriticalityIcon(iconId: string): React.FC<any> {
  return CRITICALITY_ICONS.find(i => i.id === iconId)?.Icon ?? HelpCircle;
}

const CURRENCIES: { code: string; label: string; symbol: string }[] = [
  { code: 'EUR', label: 'Euro', symbol: '€' },
  { code: 'USD', label: 'US Dollar', symbol: '$' },
  { code: 'GBP', label: 'Pound Sterling', symbol: '£' },
  { code: 'CHF', label: 'Swiss Franc', symbol: 'Fr.' },
  { code: 'JPY', label: 'Yen', symbol: '¥' },
  { code: 'CAD', label: 'Canadian Dollar', symbol: 'CA$' },
  { code: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
  { code: 'CNY', label: 'Renminbi (RMB)', symbol: '¥' },
];

function buildDefaultCurrencyRates(mainCode: string): CurrencyRate[] {
  const main = CURRENCIES.find(c => c.code === mainCode) ?? CURRENCIES[0];
  return CURRENCIES.map(c => ({
    code: c.code,
    label: c.label,
    symbol: c.symbol,
    rateToMain: c.code === mainCode ? 1 : 1,
  }));
}

interface ConfigurationViewProps {
  settings: AppSettings;
  onUpdateSettings: (settings: AppSettings) => Promise<void>;
}

export const ConfigurationView: React.FC<ConfigurationViewProps> = ({ settings, onUpdateSettings }) => {
  // On initialise avec les settings reçus OU un objet vide par sécurité
  const [localSettings, setLocalSettings] = useState<AppSettings>(settings);
  const [hasChanges, setHasChanges] = useState(false);

  // NOUVEAU : On met à jour la copie locale si les données arrivent de Supabase après coup
  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  // États pour les inputs
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [newAllowedDomain, setNewAllowedDomain] = useState('');
  const [currencyTableOpen, setCurrencyTableOpen] = useState(false);

  // Liste des devises avec taux (pour la table) : fusion des taux sauvegardés et des défauts
  const currencyRatesForTable = useMemo((): CurrencyRate[] => {
    const main = localSettings.currency || 'EUR';
    const saved = localSettings.currencyRates ?? [];
    const savedByCode = new Map(saved.map(r => [r.code, r]));
    return CURRENCIES.map(c => {
      const existing = savedByCode.get(c.code);
      return {
        code: c.code,
        label: c.label,
        symbol: c.symbol,
        rateToMain: existing?.rateToMain ?? (c.code === main ? 1 : 1),
      };
    });
  }, [localSettings.currency, localSettings.currencyRates]);

  const handleChange = (field: keyof AppSettings, value: any) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateSettings(localSettings);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field: 'categories' | 'tags' | 'teams' | 'allowedEmailDomains', value: string, resetFn: (s: string) => void) => {
    if (!value.trim()) return;
    const normalizedValue = field === 'allowedEmailDomains'
      ? value.trim().toLowerCase().replace(/^@/, '')
      : value.trim();
    if (!normalizedValue) return;
    // Sécurité : On s'assure que la liste existe (|| [])
    const currentList = (localSettings[field] || []) as string[];
    if (currentList.includes(normalizedValue)) return;

    const newList = [...currentList, normalizedValue];
    handleChange(field as keyof AppSettings, newList);
    resetFn('');
  };

  const removeItem = (field: 'categories' | 'tags' | 'teams' | 'allowedEmailDomains', value: string) => {
    const currentList = (localSettings[field] || []) as string[];
    const newList = currentList.filter(item => item !== value);
    handleChange(field as keyof AppSettings, newList);
  };

  const setMainCurrency = (code: string) => {
    const c = CURRENCIES.find(x => x.code === code) ?? CURRENCIES[0];
    const newRates = currencyRatesForTable.map(r => ({
      ...r,
      rateToMain: r.code === code ? 1 : r.rateToMain,
    }));
    setLocalSettings(prev => ({
      ...prev,
      currency: c.code,
      currencySymbol: c.symbol,
      currencyRates: newRates,
    }));
    setHasChanges(true);
  };

  const [rateInputs, setRateInputs] = useState<Record<string, string>>({});

  const updateCurrencyRate = (code: string, rawValue: string) => {
    setRateInputs(prev => ({ ...prev, [code]: rawValue }));
    const num = parseFloat(rawValue);
    if (!isNaN(num) && num > 0) {
      const newRates = currencyRatesForTable.map(r =>
        r.code === code ? { ...r, rateToMain: num } : r
      );
      setLocalSettings(prev => ({ ...prev, currencyRates: newRates }));
      setHasChanges(true);
    }
  };

  const commitCurrencyRate = (code: string) => {
    const raw = rateInputs[code];
    if (raw === undefined) return;
    const num = parseFloat(raw);
    const final = isNaN(num) || num <= 0 ? 1 : num;
    const newRates = currencyRatesForTable.map(r =>
      r.code === code ? { ...r, rateToMain: final } : r
    );
    setLocalSettings(prev => ({ ...prev, currencyRates: newRates }));
    setRateInputs(prev => { const next = { ...prev }; delete next[code]; return next; });
    setHasChanges(true);
  };

  // Si les settings n'ont pas encore chargé du tout, on affiche un chargement
  if (!localSettings) return <div className="p-8 text-center">Loading configuration...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-slide-in pb-12">
      
      {/* Header — sticky pour garder le bouton Save visible */}
      <header className="sticky top-0 z-10 relative bg-[#F8F1E5]/95 backdrop-blur-sm py-4 flex justify-between items-start gap-4 mb-8 border-b border-[#E8DDD0] -mt-4 sm:-mt-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Global Configuration
          </h1>
          <p className="text-slate-500 mt-1">Manage system-wide settings and lists.</p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="settings-save"
            className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm flex-shrink-0 disabled:opacity-50"
          >
            <Save size={18} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </header>

      {/* 1. General Settings */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Settings size={18} /> General Preferences
            <InfoTooltip text="Your base currency for all cost calculations and reports." />
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Currency Code (ISO)</label>
            <select
              data-testid="settings-currency-code"
              aria-label="Currency Code (ISO)"
              className="w-full p-2 border border-slate-300 rounded-lg bg-white"
              value={localSettings.currency || 'EUR'}
              onChange={e => {
                const code = e.target.value;
                const currency = CURRENCIES.find(c => c.code === code) ?? CURRENCIES[0];
                setLocalSettings(prev => ({ ...prev, currency: currency.code, currencySymbol: currency.symbol }));
                setHasChanges(true);
              }}
            >
              {currencyRatesForTable.map(c => (
                <option key={c.code} value={c.code}>{c.code} – {c.label} ({c.symbol})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Currency Symbol</label>
            <input
              type="text"
              data-testid="settings-currency-symbol"
              readOnly
              className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-700"
              value={localSettings.currencySymbol ?? (CURRENCIES.find(c => c.code === (localSettings.currency || 'EUR'))?.symbol ?? '€')}
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setCurrencyTableOpen(true)}
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm"
          >
            <Coins size={18} /> Manage currencies and conversion rates
          </button>
        </div>

        {/* Modal Table des devises */}
        {currencyTableOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCurrencyTableOpen(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-bold text-slate-800">Currencies and conversion rates</h3>
                <button type="button" onClick={() => setCurrencyTableOpen(false)} className="p-1 text-slate-500 hover:text-slate-700"><X size={20} /></button>
              </div>
              <p className="px-4 py-2 text-sm text-slate-500">Rate to primary currency: 1 unit = X in {localSettings.currency}.</p>
              <div className="overflow-auto flex-1 p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="pb-2 pr-2">Code</th>
                      <th className="pb-2 pr-2">Name</th>
                      <th className="pb-2 pr-2">Symbol</th>
                      <th className="pb-2 pr-2">Rate → {localSettings.currency}</th>
                      <th className="pb-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencyRatesForTable.map(r => (
                      <tr key={r.code} className="border-b border-slate-100">
                        <td className="py-2 pr-2 font-medium">{r.code}</td>
                        <td className="py-2 pr-2">{r.label}</td>
                        <td className="py-2 pr-2">{r.symbol}</td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            className="w-24 p-1.5 border border-slate-300 rounded"
                            value={rateInputs[r.code] ?? r.rateToMain}
                            onChange={e => updateCurrencyRate(r.code, e.target.value)}
                            onBlur={() => commitCurrencyRate(r.code)}
                            readOnly={r.code === (localSettings.currency || 'EUR')}
                          />
                        </td>
                        <td className="py-2">
                          {r.code !== (localSettings.currency || 'EUR') ? (
                            <button type="button" onClick={() => setMainCurrency(r.code)} className="text-indigo-600 hover:underline text-xs">Set as primary</button>
                          ) : (
                            <span className="text-slate-400 text-xs">Primary</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label htmlFor="renewal-warning-days" className="block text-sm font-medium text-slate-600 mb-1">Renewal Warning (Days)</label>
            <input 
              id="renewal-warning-days"
              type="number" 
              className="w-full p-2 border border-slate-300 rounded-lg"
              value={localSettings.renewalWarningDays || 30}
              onChange={e => {
                const parsed = Number.parseInt(e.target.value, 10);
                handleChange('renewalWarningDays', Number.isFinite(parsed) ? parsed : 30);
              }}
            />
          </div>
          <div>
            <label htmlFor="notice-warning-days" className="block text-sm font-medium text-slate-600 mb-1">Cancellation Notice Warning (Days)</label>
            <input 
              id="notice-warning-days"
              type="number" 
              className="w-full p-2 border border-slate-300 rounded-lg"
              value={localSettings.noticeWarningDays || 60}
              onChange={e => {
                const parsed = Number.parseInt(e.target.value, 10);
                handleChange('noticeWarningDays', Number.isFinite(parsed) ? parsed : 60);
              }}
            />
          </div>
        </div>
      </section>

      {/* 2. Categories Management */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Database size={18} /> Tool Categories
            <InfoTooltip text="Organize tools by type. Categories appear as filter options across the app." />
        </h3>
        <div className="flex gap-2 mb-4">
            <input 
                type="text" 
                className="flex-1 p-2 border border-slate-300 rounded-lg"
                placeholder="Add new category..."
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem('categories', newCategory, setNewCategory)}
            />
            <button 
                onClick={() => addItem('categories', newCategory, setNewCategory)}
                className="bg-slate-900 text-white p-2 rounded-lg"
            >
                <Plus size={20} />
            </button>
        </div>
        <div className="flex flex-wrap gap-2">
            {/* BLINDAGE ICI : (list || []).map */}
            {(localSettings.categories || []).filter((c): c is string => typeof c === 'string').map(cat => (
                <span key={cat} data-testid={`category-chip-${cat}`} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                    {cat}
                    <button onClick={() => removeItem('categories', cat)} className="hover:text-red-500" aria-label={`Supprimer la catégorie ${cat}`}><X size={14} /></button>
                </span>
            ))}
        </div>
      </section>

      {/* 3. Tags Management */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Tag size={18} /> Tags
            <InfoTooltip text="Free-form labels to group tools. Tags auto-sync from tool entries and allow for custom reporting." />
        </h3>
        <div className="flex gap-2 mb-4">
            <input 
                type="text" 
                className="flex-1 p-2 border border-slate-300 rounded-lg"
                placeholder="Add new tag..."
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem('tags', newTag, setNewTag)}
            />
            <button 
                onClick={() => addItem('tags', newTag, setNewTag)}
                className="bg-slate-900 text-white p-2 rounded-lg"
            >
                <Plus size={20} />
            </button>
        </div>
        <div className="flex flex-wrap gap-2">
             {/* BLINDAGE ICI AUSSI */}
            {(localSettings.tags || []).map(tag => (
                <span key={tag} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                    {tag}
                    <button onClick={() => removeItem('tags', tag)} className="hover:text-red-500"><X size={14} /></button>
                </span>
            ))}
        </div>
      </section>

      {/* 4. Teams Management */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Users size={18} /> Departments / Teams
            <InfoTooltip text="Define teams to track per-team spending, assign employees, and automate tool assignment per team." />
        </h3>
        <div className="flex gap-2 mb-4">
            <input 
                type="text" 
                className="flex-1 p-2 border border-slate-300 rounded-lg"
                placeholder="Add new team..."
                value={newTeam}
                onChange={e => setNewTeam(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem('teams', newTeam, setNewTeam)}
            />
            <button 
                onClick={() => addItem('teams', newTeam, setNewTeam)}
                className="bg-slate-900 text-white p-2 rounded-lg"
            >
                <Plus size={20} />
            </button>
        </div>
        <div className="flex flex-wrap gap-2">
            {(localSettings.teams || []).map(team => (
                <span key={team} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                    {team}
                    <button onClick={() => removeItem('teams', team)} className="hover:text-red-500"><X size={14} /></button>
                </span>
            ))}
        </div>
      </section>

      {/* 5. Criticality Levels */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Shield size={18} /> Allowed Internal Email Domains
          <InfoTooltip text="Domains considered internal for employee invitations (e.g. company.com). Invitations to other domains will show a warning." />
        </h3>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            className="flex-1 p-2 border border-slate-300 rounded-lg"
            placeholder="e.g. digitaloasis.tech"
            value={newAllowedDomain}
            onChange={e => setNewAllowedDomain(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem('allowedEmailDomains', newAllowedDomain, setNewAllowedDomain)}
          />
          <button
            onClick={() => addItem('allowedEmailDomains', newAllowedDomain, setNewAllowedDomain)}
            className="bg-slate-900 text-white p-2 rounded-lg"
          >
            <Plus size={20} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(localSettings.allowedEmailDomains || []).map((domain) => (
            <span key={domain} className="bg-violet-50 text-violet-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
              {domain}
              <button onClick={() => removeItem('allowedEmailDomains', domain)} className="hover:text-red-500"><X size={14} /></button>
            </span>
          ))}
        </div>
      </section>

      {/* 6. Criticality Levels */}
      <CriticalityLevelsSection
        levels={localSettings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS}
        onChange={(levels) => handleChange('criticalityLevels', levels)}
      />
    </div>
  );
};

// =============================================
// Criticality Levels sub-component
// =============================================
interface CriticalityLevelsSectionProps {
  levels: CriticalityLevel[];
  onChange: (levels: CriticalityLevel[]) => void;
}

function CriticalityLevelsSection({ levels, onChange }: CriticalityLevelsSectionProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<CriticalityLevel | null>(null);
  const sorted = [...levels].sort((a, b) => a.sortOrder - b.sortOrder);

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setDraft({ ...sorted[idx] });
  };

  const MAX_LEVELS = 5;

  const startAdd = () => {
    if (levels.length >= MAX_LEVELS) return;
    const maxOrder = sorted.length > 0 ? Math.max(...sorted.map(l => l.sortOrder)) : -1;
    setDraft({
      id: '',
      label: '',
      color: 'blue',
      icon: 'HelpCircle',
      reviewFrequencyMonths: 6,
      governancePriority: 'MEDIUM',
      sortOrder: maxOrder + 1,
    });
    setEditingIdx(-1);
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setDraft(null);
  };

  const saveEdit = () => {
    if (!draft || !draft.label.trim()) return;
    const finalDraft = { ...draft, id: draft.id || draft.label.replace(/\s+/g, '_') };
    let updated: CriticalityLevel[];
    if (editingIdx === -1) {
      updated = [...levels, finalDraft];
    } else {
      updated = levels.map(l => l.id === sorted[editingIdx!].id ? finalDraft : l);
    }
    onChange(updated);
    cancelEdit();
  };

  const removeLevel = (id: string) => {
    if (levels.length <= 1) return;
    if (!window.confirm('Remove this criticality level? Tools using it will need to be reassigned.')) return;
    onChange(levels.filter(l => l.id !== id));
  };

  const moveLevel = (idx: number, direction: -1 | 1) => {
    const target = idx + direction;
    if (target < 0 || target >= sorted.length) return;
    const reordered = [...sorted];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    onChange(reordered.map((l, i) => ({ ...l, sortOrder: i })));
  };

  return (
    <section data-testid="criticality-levels-section" className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <h3 className="font-bold text-slate-700 mb-1 flex items-center gap-2">
        <Shield size={18} /> Criticality Levels
        <InfoTooltip text="Define criticality levels for tools. Each level controls governance review frequency and task priority." />
      </h3>
      <p className="text-sm text-slate-500 mb-4">Levels determine how often tools are reviewed and at what priority governance tasks are created.</p>

      <div className="space-y-2 mb-4">
        {sorted.map((level, idx) => {
          const colorCls = getCriticalityColorClasses(level.color);
          const IconComp = getCriticalityIcon(level.icon);
          return (
            <div key={level.id} data-testid={`criticality-level-${level.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveLevel(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => moveLevel(idx, 1)} disabled={idx === sorted.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <span className={`w-8 h-8 rounded-full ${colorCls.bg} ${colorCls.border} border flex items-center justify-center ${colorCls.text} shrink-0`}>
                <IconComp size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-slate-800">{level.label}</span>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                  <span>Review every {level.reviewFrequencyMonths}mo</span>
                  <span>Priority: {level.governancePriority}</span>
                </div>
              </div>
              <button data-testid={`criticality-edit-${level.id}`} onClick={() => startEdit(idx)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100"><Pencil size={14} /></button>
              <button data-testid={`criticality-delete-${level.id}`} onClick={() => removeLevel(level.id)} disabled={levels.length <= 1} className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50 disabled:opacity-30"><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>

      {editingIdx !== null && draft && (
        <div className="border border-indigo-200 bg-indigo-50/50 rounded-lg p-4 mb-4 space-y-3">
          <h4 data-testid="criticality-form-title" className="font-medium text-slate-800 text-sm">{editingIdx === -1 ? 'Add Level' : 'Edit Level'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Label *</label>
              <input
                data-testid="criticality-label-input"
                type="text"
                value={draft.label}
                onChange={e => setDraft({ ...draft, label: e.target.value })}
                className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                placeholder="e.g. Mission Critical"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Color</label>
              <div className="flex gap-2">
                {CRITICALITY_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setDraft({ ...draft, color: c.id })}
                    className={`w-8 h-8 rounded-full ${c.bg} ${c.border} border flex items-center justify-center ${c.text} ${draft.color === c.id ? 'ring-2 ring-offset-1 ring-indigo-500' : ''}`}
                    title={c.label}
                  >
                    {draft.color === c.id && <span className="text-xs font-bold">✓</span>}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Icon</label>
              <div className="flex gap-2">
                {CRITICALITY_ICONS.map(ic => (
                  <button
                    key={ic.id}
                    type="button"
                    onClick={() => setDraft({ ...draft, icon: ic.id })}
                    className={`w-8 h-8 rounded-lg border flex items-center justify-center text-slate-600 ${draft.icon === ic.id ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                    title={ic.label}
                  >
                    <ic.Icon size={16} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Governance Priority</label>
              <select
                data-testid="criticality-priority-select"
                value={draft.governancePriority}
                onChange={e => setDraft({ ...draft, governancePriority: e.target.value as 'HIGH' | 'MEDIUM' | 'LOW' })}
                className="w-full p-2 border border-slate-300 rounded-lg bg-white text-sm"
              >
                <option value="HIGH">HIGH</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="LOW">LOW</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Review Frequency (months)</label>
              <input
                data-testid="criticality-frequency-input"
                type="number"
                min={1}
                max={60}
                value={draft.reviewFrequencyMonths}
                onChange={e => setDraft({ ...draft, reviewFrequencyMonths: Math.max(1, Number(e.target.value) || 1) })}
                className="w-full p-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="criticality-save-btn" onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              {editingIdx === -1 ? 'Add' : 'Save'}
            </button>
            <button data-testid="criticality-cancel-btn" onClick={cancelEdit} className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {editingIdx === null && levels.length < MAX_LEVELS && (
        <button data-testid="criticality-add-btn" onClick={startAdd} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm">
          <Plus size={16} /> Add criticality level
        </button>
      )}
      {editingIdx === null && levels.length >= MAX_LEVELS && (
        <p className="text-xs text-slate-400 italic">Maximum {MAX_LEVELS} levels reached.</p>
      )}
    </section>
  );
}